import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, Typography, Alert, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Spinner from '../components/Spinner';

interface ExamTicketSubject {
  subjectCode: string;
  subjectName: string;
  date?: string;
}

interface ExamTicket {
  studentName: string;
  studentId: string;
  studentRegistrationId?: string | null;
  studentPhoto?: string;
  schoolName: string;
  schoolLogoUrl?: string;
  className: string;
  termName: string;
  termStartDate?: string;
  termEndDate?: string;
  attendancePercentage: number;
  isEligibleForExam: boolean;
  examVenue: string;
  examTicketSubjects: ExamTicketSubject[];
  examTicketSignatureUrl?: string;
  examTicketInstructions?: string;
  generatedAt: string;
}

const StudentExamTicket: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<ExamTicket | null>(null);
  const [printTimestamp, setPrintTimestamp] = useState<string>('');

  useEffect(() => {
    const fetchTicket = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/school-work/exam-ticket');
        setTicket({
          ...response.data,
          generatedAt: response.data.generatedAt || new Date().toISOString()
        });
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load exam ticket information');
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, []);

  useEffect(() => {
    if (!ticket) return;
    const previousTitle = document.title;
    document.title = `${ticket.schoolName}${ticket.termName ? ` - ${ticket.termName}` : ''}`;
    return () => {
      document.title = previousTitle;
    };
  }, [ticket]);

  const handlePrint = () => {
    if (ticket) {
      document.title = `${ticket.schoolName}${ticket.termName ? ` - ${ticket.termName}` : ''}`;
    }
    const now = new Date().toLocaleString();
    setPrintTimestamp(now);
    window.setTimeout(() => {
      window.print();
    }, 150);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box component="style">
        {`@media print {
          @page { margin: 14mm; }
          body * { visibility: hidden !important; }
          #ticket-print-area, #ticket-print-area * { visibility: visible !important; }
          #ticket-print-area { position: absolute; top: 0; left: 0; width: 100%; background: #fff; }
          #ticket-print-area > .MuiPaper-root { border: 1px solid rgba(148, 163, 184, 0.5) !important; box-shadow: none !important; }
          .no-print { display: none !important; }
          #ticket-print-datetime { display: block !important; }
        }`}
      </Box>
      <Button className="no-print" variant="outlined" sx={{ mb: 2 }} onClick={() => navigate(-1)}>
        Back
      </Button>
      <Typography className="no-print" variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        Exam Ticket Preview
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : ticket ? (
        <Box id="ticket-print-area">
          <Paper sx={{ p: 4, mb: 3, borderRadius: 3, backgroundColor: '#ffffff', border: '1px solid rgba(148, 163, 184, 0.45)', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {ticket.schoolLogoUrl ? (
                  <Box component="img" src={ticket.schoolLogoUrl} alt="School logo" sx={{ width: 70, height: 70, objectFit: 'contain', borderRadius: 1, border: '1px solid rgba(0,0,0,0.08)' }} />
                ) : (
                  <Box sx={{ width: 70, height: 70, backgroundColor: 'primary.main', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
                    {ticket.schoolName ? ticket.schoolName.charAt(0).toUpperCase() : 'S'}
                  </Box>
                )}
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1, color: '#0f172a' }}>{ticket.schoolName}</Typography>
                  <Typography sx={{ mt: 0.5, color: '#475569', opacity: 0.95 }}>Exam Ticket / Hall Ticket</Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 160 }}>
                <Typography id="ticket-print-datetime" sx={{ display: printTimestamp ? 'block' : 'none', color: 'text.secondary', fontSize: 13, mb: 0.5 }}>
                  Printed: {printTimestamp}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1, display: ticket.termName ? 'block' : 'none' }}>
                  Exam Term: {ticket.termName}
                </Typography>
                {ticket.studentPhoto ? (
                  <Box sx={{ width: 100, height: 100, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                    <Box component="img" src={ticket.studentPhoto} alt="Student" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ) : null}
              </Box>
            </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#475569' }}>Student Name</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>{ticket.studentName}</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#475569' }}>Class</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>{ticket.className}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#475569' }}>Exam Session</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>{ticket.termName}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#475569' }}>Student ID</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>{ticket.studentId}</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#475569' }}>Exam Venue</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>{ticket.examVenue || 'Not specified'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#475569' }}>Attendance</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>{ticket.attendancePercentage.toFixed(2)}%</Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569' }}>Subject Schedule</Typography>
          {ticket.examTicketSubjects && ticket.examTicketSubjects.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.55)', overflow: 'hidden', backgroundColor: '#f8fafc' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e2e8f0' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#0f172a', borderBottom: '1px solid rgba(148, 163, 184, 0.6)' }}>Subject Code</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0f172a', borderBottom: '1px solid rgba(148, 163, 184, 0.6)' }}>Subject Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0f172a', borderBottom: '1px solid rgba(148, 163, 184, 0.6)' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ticket.examTicketSubjects.map((subject, index) => (
                    <TableRow key={`${subject.subjectCode}-${index}`} sx={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <TableCell sx={{ borderBottom: index === ticket.examTicketSubjects.length - 1 ? 'none' : '1px solid rgba(148, 163, 184, 0.35)', color: '#0f172a' }}>{subject.subjectCode || '—'}</TableCell>
                      <TableCell sx={{ borderBottom: index === ticket.examTicketSubjects.length - 1 ? 'none' : '1px solid rgba(148, 163, 184, 0.35)', color: '#0f172a' }}>{subject.subjectName || '—'}</TableCell>
                      <TableCell sx={{ borderBottom: index === ticket.examTicketSubjects.length - 1 ? 'none' : '1px solid rgba(148, 163, 184, 0.35)', color: '#0f172a' }}>{subject.date ? new Date(subject.date).toLocaleDateString() : 'Not set'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>No examination subjects configured yet.</Typography>
          )}

          {ticket.examTicketInstructions ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#475569' }}>Instructions to Candidates</Typography>
              <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-line', color: '#0f172a' }}>
                {ticket.examTicketInstructions}
              </Typography>
            </Box>
          ) : null}

          {ticket.examTicketSignatureUrl ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Controller of Examination</Typography>
              <Box component="img" src={ticket.examTicketSignatureUrl} alt="Controller Signature" sx={{ maxWidth: 240, maxHeight: 120, objectFit: 'contain', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }} />
            </Box>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button className="no-print" variant="contained" onClick={handlePrint}>Print Ticket</Button>
          </Box>
        </Paper>
      </Box>
      ) : (
        <Alert severity="info">No exam ticket data available yet.</Alert>
      )}
    </Box>
  );
};

export default StudentExamTicket;
